'use client'

import { html } from '@ensemble.music/ui'
import { motion, useMotionValueEvent, useScroll, useTransform } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { type Step, steps as bookerSteps } from './content'
import { SectionHeading } from './SectionHeading'

/**
 * "So läuft es ab" as a pinned, scroll-driven sequence (owner request 2026-10-18): the block sticks
 * below the nav while the grey rail fills blue from dot 1 to dot 3, each dot turning blue as the
 * fill reaches it, and then the page scrolls on. The pin, the rail and the dot colours live in the
 * .landing-steps-* rules in style.css, which also drop the pin and show the finished state under
 * prefers-reduced-motion and on phones.
 */
export function HowItWorks({
  steps = bookerSteps,
  title = 'So läuft es ab',
  lead = 'Eine Anfrage, drei Angebote, eine Entscheidung. Den Rest machen wir.',
}: {
  steps?: readonly Step[]
  title?: string
  lead?: string
} = {}) {
  const sectionRef = useRef<HTMLElement>(null)
  const pinned = usePinned()
  // Pinned: from the section top reaching the scrolled nav (where the pin starts, 56 px =
  // .landing-steps-pin top) until its end reaches the screen bottom (where it lets go). On a phone
  // there is no pin and the section is shorter than the screen, so the fill follows the block
  // passing the lower quarter of the screen instead.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: pinned ? ['start 56px', 'end end'] : ['start 75%', 'end 75%'],
  })
  // A short lead-in and run-out, so the fill starts once the pin holds and ends before it lets go.
  const fill = useTransform(scrollYProgress, [0.02, 0.92], [0, 1])
  const [reached, setReached] = useState(0)

  // A dot turns blue the moment the fill touches it: dot 1 at the start, 2 at half, 3 at the end.
  // Re-renders only when that count changes, not on every scroll frame.
  useMotionValueEvent(fill, 'change', (value) => {
    const next = value <= 0 ? 0 : value < 0.5 ? 1 : value < 1 ? 2 : 3
    if (next !== reached) setReached(next)
  })

  return (
    // custom classes stay on plain DOM elements: mixing one into an html.* className makes
    // Tamagui fall back to inline styles, which then differ between server and client.
    <section
      ref={sectionRef}
      id="so-funktionierts"
      className="landing-steps-scroll"
    >
      <div className="landing-steps-pin">
        <SectionHeading
          layout="split"
          size="lg"
          title={title}
          lead={lead}
        />

        <div className="landing-steps">
          {/* decorative: the step numbers already say the order */}
          <div
            aria-hidden="true"
            className="landing-steps-rail"
          >
            <motion.div
              className="landing-steps-rail-fill"
              style={{ scaleX: fill }}
            />
          </div>

          {steps.map((step, index) => (
            <div
              key={step.title}
              className="landing-step"
            >
              {/* The number is decorative - the heading below carries the meaning. */}
              <span
                aria-hidden="true"
                className="landing-step-dot"
                data-reached={index < reached}
              >
                {String(index + 1)}
              </span>

              <html.div className="flex flex-col gap-3 w-full max-w-[320px]">
                <html.div className="flex flex-row items-center gap-3">
                  <html.span className="inline-flex flex-row items-center justify-center w-11 h-11 rounded-xl bg-primary-soft text-primary-text">
                    <step.icon
                      size={22}
                      aria-hidden="true"
                    />
                  </html.span>
                  <html.h3 className="text-2xl font-bold tracking-tight text-color max-sm:text-xl">
                    {step.title}
                  </html.h3>
                </html.div>
                <html.p className="text-base text-color-secondary">{step.body}</html.p>
              </html.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/** Mirrors the 768 px breakpoint in style.css below which .landing-steps-pin stops sticking. */
function usePinned() {
  const [pinned, setPinned] = useState(true)
  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)')
    setPinned(query.matches)
    const onChange = () => setPinned(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])
  return pinned
}
